import { useSavedSearchMetadata } from "./useSavedSearchMetadata";
import { useSavedSearchFilters } from "./useSavedSearchFilters";
import { cancelSavedSearchRequests, useSavedSearchEnrichment, enrichmentCompanyIds } from "./useSavedSearchEnrichment";
import {useSavedSearchBatchRecovery} from "./useSavedSearchBatchRecovery";
import { SavedSearchDetailView } from "./SavedSearchDetailView";
import { isCompanyActionEligible } from "../../helper/companyEligibility";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import MainLayout from "../../components/layouts/MainLayout";

import { searchApi } from "../../api/searchApi";
import { getWorkspaceDetailApi } from "../../api/workspaceApi";

import { errorDetail } from "../../helper/errorDetail";

import { DEFAULT_WORKSPACE_PROVIDER } from "../../helper/aiEnrichment";

import { useEnrichmentQuote } from "./enrichmentQuote";

import { isMockSearchCompaniesEnabled, MOCK_SEARCH_COMPANIES } from "../../fixture/mockSearchCompanies";
import { buildMockSavedSearchDetail, isMockSavedSearchId, isMockSavedSearchesEnabled } from "../../fixture/mockSavedSearches";
import { buildWorkspaceEnrichmentSummaryStatusMap, buildWorkspaceEnrichmentStatusMap, extractWorkspaceEnrichmentColumnKeys, indexWorkspaceEnrichmentsByCompanyId, mapWorkspaceRowsToCompanies, mapWorkspaceToSavedSearchOption, mergeWorkspaceEnrichmentStatusMaps, mergeWorkspaceEnrichmentsOntoCompanies } from "./savedSearchQuery";
import { useSavedSearchExport } from "./useSavedSearchExport";
import { useSavedSearchHistory } from "./useSavedSearchHistory";
import { Wrapper } from "./workspaceGridComponents";
import { FIXED_HEADER_OFFSET } from "../../components/layouts/headerLayout";
import useIsMobile from "../../hooks/useIsMobile";
import { useOnboardingActionRun } from "../Onboarding/actions/useOnboardingActionRun";
import OnboardingActionsBanner from "../Onboarding/actions/OnboardingActionsBanner";
import withHomeExecutionReview from "../../components/withHomeExecutionReview";
import useSavedSearchNewResults from "./useSavedSearchNewResults";
import { snapshotPaginationProps, useSavedSearchSnapshotPager } from "./useSavedSearchSnapshotPager";
import { useLiveSearchPaging } from "./useLiveSearchPaging";

const MOBILE_STICKY_SHELL_CLEARANCE_PX = 24;

/**
 * V4 Saved Search detail — Search page chrome + Enrich Workspace.
 * Filters stay editable; Save creates a new saved search when they diverge.
 * Banner Edit search remains a rename-only action for the current workspace.
 */

export default withHomeExecutionReview(SavedSearchDetail);

function SavedSearchDetail() {
  const { id } = useParams();
  const detailRouteRef = useRef(id);
  detailRouteRef.current = id;
  const detailRequestRef = useRef(0);
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const abortControllerRef = useRef(null);
  const pollTimerRef = useRef(null);
  /** Keep the saved membership visible until the user edits a filter. */
  const preferWorkspaceSnapshotRef = useRef(false);
  const enrichmentByCompanyIdRef = useRef({});
  const [workspace, setWorkspace] = useState(null);
  const [workspaceLoading, setWorkspaceLoading] = useState(true);
  const snapshotPager = useSavedSearchSnapshotPager(id);
  const requestedSnapshotPage = snapshotPager.requested;
  const [error, setError] = useState(null);
  /** V4: filter toolbar starts collapsed beside the saved-search title. */
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const {
    originalSavedFilters, setOriginalSavedFilters, termsInclude, termsExclude,
    industryInclude, industryExclude, locations, revenueLowerBound,
    revenueUpperBound, employeesLowerBound, employeesUpperBound, naturalQuery,
    filtersHydrated, setFiltersHydrated, hasNaturalQuery, hydrateFiltersFromWorkspace,
    unlockLiveSearch, setTermsIncludeLive, setTermsExcludeLive, setIndustryIncludeLive,
    setIndustryExcludeLive, setLocationsLive, setRevenueLowerBoundLive, setRevenueUpperBoundLive,
    setEmployeesLowerBoundLive, setEmployeesUpperBoundLive, setNaturalQueryLive, activeQuery,
    isSavedSearchDirty, saveSearchDisabled
  } = useSavedSearchFilters({
    preferWorkspaceSnapshotRef
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [searchLoading, setSearchLoading] = useState(false);
  const [resultsSource, setResultsSource] = useState("snapshot");
  const livePaging = useLiveSearchPaging();
  const { paginationFor: livePaginationFor, recordPage: recordLivePage } = livePaging;
  const showingWorkspaceSnapshot = resultsSource === "snapshot";
  const [selectedIds, setSelectedIds] = useState(new Set());
  const normalizedWorkspace = useMemo(() => mapWorkspaceToSavedSearchOption(workspace), [workspace]);
  const savedSearchOption = normalizedWorkspace || { id, name: workspace?.name };
  const historical = useSavedSearchHistory(id, workspace, detailRouteRef);
  const ingestHistorical = historical.ingest;
  const savedSearchExport = useSavedSearchExport({ getWorkspaceDetail: getWorkspaceDetailApi, id, isWorkspaceLoaded: !workspaceLoading && normalizedWorkspace?.id === id, isWorkspaceSnapshot: showingWorkspaceSnapshot, historicalRows: historical.rows, selectedIds, setError, setSelectedIds, totalRows, workspace, workspaceId: normalizedWorkspace?.id });
  const [workspaceMemberIds, setWorkspaceMemberIds] = useState([]);
  const [workspaceEnrichmentColumns, setWorkspaceEnrichmentColumns] = useState([]);
  const [workspaceEnrichmentColumnLabels, setWorkspaceEnrichmentColumnLabels] = useState({});
  const [liveEnrichmentStatusByColumn, setLiveEnrichmentStatusByColumn] = useState({});
  const [focusGeneratedColumnKey, setFocusGeneratedColumnKey] = useState(null);
  const enrichPollCompletedRef = useRef(0);
  const [enrichQuery, setEnrichQuery] = useState("");
  const [columnTitle, setColumnTitle] = useState("");
  const [llmProvider, setLlmProvider] = useState(DEFAULT_WORKSPACE_PROVIDER);
  const [enriching, setEnriching] = useState(false);
  const [batchStatus, setBatchStatus] = useState(null);
  const [enrichPanelCollapsed, setEnrichPanelCollapsed] = useState(false);
  const enrichmentGridRef = useRef(null);
  const enrichmentStickyShellRef = useRef(null);
  const [openSaveSearchDialog, setOpenSaveSearchDialog] = useState(false);
  const [saveSearchSnackbar, setSaveSearchSnackbar] = useState({
    open: false,
    message: "",
  });

  const ingestWorkspaceEnrichment = useCallback((detail, page = 1) => {
    // Retained answers page on their own "Load more"; a later grid page must not reset them.
    if (page <= 1) ingestHistorical(detail);
    const enrichmentCols = extractWorkspaceEnrichmentColumnKeys(detail?.columns || []);
    setWorkspaceEnrichmentColumns(enrichmentCols);
    setWorkspaceEnrichmentColumnLabels(detail?.column_labels && typeof detail.column_labels === "object" ? { ...detail.column_labels } : {});
    enrichmentByCompanyIdRef.current = indexWorkspaceEnrichmentsByCompanyId(
      detail?.rows || [],
      enrichmentCols
    );
    const snapshotCompanies = mapWorkspaceRowsToCompanies(detail?.rows || [], enrichmentCols);
    setWorkspaceMemberIds(snapshotCompanies.filter(isCompanyActionEligible).map((company) => company.stagedId));
    return { enrichmentCols, snapshotCompanies };
  }, [ingestHistorical]);

  const fetchWorkspaceDetail = useCallback(async () => {
    if (detailRouteRef.current !== id) return;
    const requestId = ++detailRequestRef.current;
    const isCurrent = () => detailRouteRef.current === id && detailRequestRef.current === requestId;
    preferWorkspaceSnapshotRef.current = true;
    setResultsSource((current) => current === "snapshot" ? current : "stale");
    setWorkspaceLoading(true);
    setError(null);
    try {
      // Local design mode: mock saved-search routes use the Search mock company set.
      if (
        (isMockSavedSearchesEnabled() || isMockSearchCompaniesEnabled())
        && isMockSavedSearchId(id)
      ) {
        const mockDetail = buildMockSavedSearchDetail(id);
        setWorkspace(mockDetail);
        ingestWorkspaceEnrichment(mockDetail);
        setCompanies(MOCK_SEARCH_COMPANIES);
        setTotalRows(MOCK_SEARCH_COMPANIES.length);
        setWorkspaceMemberIds(MOCK_SEARCH_COMPANIES.map((row) => row.stagedId));
        preferWorkspaceSnapshotRef.current = true;
        setResultsSource("snapshot");
        setSelectedIds(new Set());
        hydrateFiltersFromWorkspace(mockDetail);
        setFiltersHydrated(true);
        return;
      }

      const { page, pageSize } = requestedSnapshotPage();
      const res = await getWorkspaceDetailApi(id, page, pageSize);
      if (!isCurrent()) return;
      if (res?.status === 200 && res.data) {
        setWorkspace(res.data);
        const { snapshotCompanies } = ingestWorkspaceEnrichment(res.data, page);

        // Always bind the grid to this workspace's data — never keep companies
        // from a previously opened saved search when the route id changes.
        if (snapshotCompanies.length > 0) {
          setCompanies(snapshotCompanies);
          setTotalRows(res.data.company_count ?? snapshotCompanies.length);
        } else if (isMockSearchCompaniesEnabled()) {
          // Design mode fallback when the workspace has no member snapshot yet.
          setCompanies(MOCK_SEARCH_COMPANIES);
          setTotalRows(MOCK_SEARCH_COMPANIES.length);
          preferWorkspaceSnapshotRef.current = true;
        } else {
          setCompanies([]);
          setTotalRows(res.data.company_count ?? 0);
        }

        // Hydrate with raw setters so we do not unlock snapshot mode.
        setResultsSource("snapshot");
        setSelectedIds(new Set());
        hydrateFiltersFromWorkspace(res.data);
      } else if (res?.status === 404) {
        if (isMockSearchCompaniesEnabled()) {
          const mockDetail = buildMockSavedSearchDetail(id || "mock-saved-preview");
          setWorkspace(mockDetail);
          setCompanies(MOCK_SEARCH_COMPANIES);
          setTotalRows(MOCK_SEARCH_COMPANIES.length);
          setWorkspaceMemberIds(MOCK_SEARCH_COMPANIES.map((row) => row.stagedId));
          preferWorkspaceSnapshotRef.current = true;
          setResultsSource("snapshot");
          setSelectedIds(new Set());
          hydrateFiltersFromWorkspace(mockDetail);
          setFiltersHydrated(true);
        } else {
          preferWorkspaceSnapshotRef.current = false;
          navigate("/workspaces");
        }
      } else {
        preferWorkspaceSnapshotRef.current = false;
        setError(errorDetail(res?.data?.detail, "Failed to load workspace details"));
        setFiltersHydrated(true);
      }
    } catch (_err) {
      if (!isCurrent()) return;
      if (isMockSearchCompaniesEnabled()) {
        const mockDetail = buildMockSavedSearchDetail(id || "mock-saved-preview");
        setWorkspace(mockDetail);
        setCompanies(MOCK_SEARCH_COMPANIES);
        setTotalRows(MOCK_SEARCH_COMPANIES.length);
        setWorkspaceMemberIds(MOCK_SEARCH_COMPANIES.map((row) => row.stagedId));
        preferWorkspaceSnapshotRef.current = true;
        setResultsSource("snapshot");
        setSelectedIds(new Set());
        hydrateFiltersFromWorkspace(mockDetail);
        setFiltersHydrated(true);
      } else {
        preferWorkspaceSnapshotRef.current = false;
        setError("Failed to load workspace details");
        setFiltersHydrated(true);
      }
    } finally {
      if (isCurrent()) setWorkspaceLoading(false);
    }
  }, [hydrateFiltersFromWorkspace, id, ingestWorkspaceEnrichment, navigate, requestedSnapshotPage, setFiltersHydrated]);

  useEffect(() => {
    fetchWorkspaceDetail();
  }, [fetchWorkspaceDetail]);

  const batchRecovery = useSavedSearchBatchRecovery({
    resourceId: id,
    batchIds: workspace?.id === id && workspace?.record_type === "saved_search" ? workspace.batch_ids : [],
    setEnriching, setBatchStatus, refreshDetail: fetchWorkspaceDetail,
  });

  useEffect(() => {
    return () => {
      detailRequestRef.current += 1;
      cancelSavedSearchRequests(pollTimerRef, abortControllerRef);
    };
  }, []);

  const performSearch = useCallback(async (targetPage = 0) => {
    if (!filtersHydrated) return;

    // Keep Enrich Workspace snapshot on screen until the user edits filters.
    if (preferWorkspaceSnapshotRef.current) {
      return;
    }

    setResultsSource((current) => current === "live" ? "stale" : current);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setSearchLoading(true);
    setError(null);

    try {
      // Local design mode: same mock company set as Search.
      if (isMockSearchCompaniesEnabled()) {
        const withEnrichment = mergeWorkspaceEnrichmentsOntoCompanies(
          MOCK_SEARCH_COMPANIES,
          enrichmentByCompanyIdRef.current,
          workspaceEnrichmentColumns
        );
        setCompanies(withEnrichment);
        setResultsSource("live");
        setTotalRows(MOCK_SEARCH_COMPANIES.length);
        recordLivePage(0, null);
        setSelectedIds(new Set());
        return;
      }

      const res = await searchApi(
        {
          query: activeQuery,
          pagination: livePaginationFor(targetPage),
        },
        { signal: controller.signal }
      );
      if (res?.data) {
        if (preferWorkspaceSnapshotRef.current) {
          return;
        }
        const fetched = res.data.companies || [];
        const withEnrichment = mergeWorkspaceEnrichmentsOntoCompanies(
          fetched,
          enrichmentByCompanyIdRef.current,
          workspaceEnrichmentColumns
        );
        setCompanies(withEnrichment);
        // The pager follows next_cursor, so every row of total_count is reachable.
        setTotalRows(res.data.total_count ?? fetched.length);
        recordLivePage(targetPage, res.data.next_cursor);
        setResultsSource("live");
        setSelectedIds(new Set());
      }
    } catch (err) {
      if (axios.isCancel(err)) return;
      if (preferWorkspaceSnapshotRef.current) return;
      if (isMockSearchCompaniesEnabled()) {
        setCompanies(MOCK_SEARCH_COMPANIES);
        setTotalRows(MOCK_SEARCH_COMPANIES.length);
        setSelectedIds(new Set());
        return;
      }
      setError("Failed to load companies for this saved search.");
    } finally {
      if (abortControllerRef.current === controller) {
        setSearchLoading(false);
        abortControllerRef.current = null;
      }
    }
  }, [activeQuery, filtersHydrated, livePaginationFor, recordLivePage, workspaceEnrichmentColumns]);

  useEffect(() => {
    performSearch();
  }, [performSearch]);

  const handleLivePageChange = useCallback((nextPage) => {
    if (nextPage === livePaging.page || searchLoading || !livePaging.canLoadPage(nextPage)) return;
    performSearch(nextPage);
  }, [livePaging, performSearch, searchLoading]);

  const runSavedQuery = useCallback(() => {
    unlockLiveSearch();
    performSearch();
  }, [performSearch, unlockLiveSearch]);

  const newResults = useSavedSearchNewResults({
    savedSearchId: id, entryState: location.state, companies, ready: !workspaceLoading && !searchLoading,
  });

  const showResultsEmptyState = useMemo(() => {
    return !searchLoading && (companies?.length || 0) === 0;
  }, [companies?.length, searchLoading]);

  // Same rule as Search (V4): Save stays off until filters diverge from the loaded baseline.

  const {
    handleSavedSearchCreated, handleDelete, handleEditSearchSuccess, handleSavedSearchBaselineChange
  } = useSavedSearchMetadata({
    id, setIsDeleting, setError, setDeleteDialogOpen,
    navigate, setSaveSearchSnackbar, setWorkspace, setOriginalSavedFilters
  });

  useEffect(() => {
    if (!isMobile || !enrichPanelCollapsed) return;
    const grid = enrichmentGridRef.current;
    const stickyShellHeight = enrichmentStickyShellRef.current
      ?.getBoundingClientRect?.().height;
    if (grid && Number.isFinite(stickyShellHeight)) {
      const reservedHeight = Math.ceil(
        stickyShellHeight + MOBILE_STICKY_SHELL_CLEARANCE_PX
      );
      grid.style.scrollMarginTop = `calc(${FIXED_HEADER_OFFSET} + ${reservedHeight}px)`;
    }
    grid?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, [enrichPanelCollapsed, isMobile]);

  const enrichScopeIds = enrichmentCompanyIds(selectedIds, workspaceMemberIds, companies, showingWorkspaceSnapshot);
  const enrichQuote = useEnrichmentQuote({ workspaceId: id, companyIds: enrichScopeIds, llmProvider, enabled: showingWorkspaceSnapshot });

  // No batch exists when submission is refused, so the backend never created this column:
  // undo the optimistic state instead of leaving a column of failed cells.

  const {
    handleEnrich
  } = useSavedSearchEnrichment({
    setLiveEnrichmentStatusByColumn, pollTimerRef, setError, setEnriching,
    setBatchStatus, enrichPollCompletedRef, preferWorkspaceSnapshotRef, fetchWorkspaceDetail,
    isMobile, setEnrichPanelCollapsed, enrichQuery, columnTitle,
    selectedIds, workspaceMemberIds, companies, enrichQuote,
    llmProvider, workspaceEnrichmentColumns, workspaceEnrichmentColumnLabels, liveEnrichmentStatusByColumn,
    setWorkspaceEnrichmentColumns, setWorkspaceEnrichmentColumnLabels, setFocusGeneratedColumnKey, setEnrichQuery,
    setColumnTitle, id
  });

  const onboardingActions = useOnboardingActionRun({ workspaceId: id, companyIds: workspaceMemberIds, onBatchSettled: fetchWorkspaceDetail });

  const handleSpreadsheetSelectionChange = useCallback((nextSelected) => {
    setSelectedIds(nextSelected instanceof Set ? nextSelected : new Set(nextSelected || []));
  }, []);

  const handleFocusGeneratedColumnConsumed = useCallback(() => {
    setFocusGeneratedColumnKey(null);
  }, []);

  const {
    workspaceEnrichmentStatusMap,
    workspaceEnrichmentSummaryStatusMap,
  } = useMemo(() => {
    const hydratedStatusMap = buildWorkspaceEnrichmentStatusMap(
      companies,
      workspaceEnrichmentColumns
    );
    return {
      workspaceEnrichmentStatusMap: mergeWorkspaceEnrichmentStatusMaps(
        hydratedStatusMap,
        liveEnrichmentStatusByColumn
      ),
      workspaceEnrichmentSummaryStatusMap: buildWorkspaceEnrichmentSummaryStatusMap(
        hydratedStatusMap,
        liveEnrichmentStatusByColumn
      ),
    };
  }, [companies, liveEnrichmentStatusByColumn, workspaceEnrichmentColumns]);

  const filterSectionProps = {
    locations,
    setLocations: setLocationsLive,
    termsInclude,
    setTermsInclude: setTermsIncludeLive,
    termsExclude,
    setTermsExclude: setTermsExcludeLive,
    industryInclude,
    setIndustryInclude: setIndustryIncludeLive,
    industryExclude,
    setIndustryExclude: setIndustryExcludeLive,
    revenueLowerBound,
    setRevenueLowerBound: setRevenueLowerBoundLive,
    revenueUpperBound,
    setRevenueUpperBound: setRevenueUpperBoundLive,
    employeesLowerBound,
    setEmployeesLowerBound: setEmployeesLowerBoundLive,
    employeesUpperBound,
    setEmployeesUpperBound: setEmployeesUpperBoundLive,
    naturalQuery,
    setNaturalQuery: setNaturalQueryLive,
    onClear: () => {},
    savedSearchesRefreshTrigger: 0,
    isNaturalLanguageMode: hasNaturalQuery,
    savedSearchCatalog: "workspace",
    applyPinnedDefault: false,
    isSavedSearchDirty,
    savedSearchBaseline: originalSavedFilters,
    onSavedSearchBaselineChange: handleSavedSearchBaselineChange,
    onEditSearchSuccess: handleEditSearchSuccess,
  };

  const goToSnapshotPage = snapshotPager.goTo;
  const snapshotPagination = showingWorkspaceSnapshot ? snapshotPaginationProps({
    pager: snapshotPager, totalRows, disabled: workspaceLoading,
    onChange: (page, pageSize) => { goToSnapshotPage(page, pageSize); fetchWorkspaceDetail(); },
  }) : null;

  const bannerCount = showingWorkspaceSnapshot
    ? totalRows || workspace?.company_count || 0
    : totalRows;

  if (workspaceLoading && !workspace) {
    return (
      <MainLayout>
        <Wrapper>
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        </Wrapper>
      </MainLayout>
    );
  }

  return <SavedSearchDetailView {...{
      id,
      filterSectionProps, workspace, bannerCount, isSavedSearchDirty,
      savedSearchOption, setDeleteDialogOpen, setOpenSaveSearchDialog, saveSearchDisabled,
      savedSearchExport, filtersExpanded, setFiltersExpanded, resultsSource,
      runSavedQuery, originalSavedFilters, searchLoading, fetchWorkspaceDetail,
      error, batchRecovery, enrichmentStickyShellRef, enrichQuery, setEnrichQuery,
      columnTitle, setColumnTitle, llmProvider, setLlmProvider,
      enriching, batchStatus, isDeleting, showingWorkspaceSnapshot,
      selectedIds, enrichScopeIds, handleEnrich, enrichQuote,
      workspaceEnrichmentColumns, workspaceEnrichmentColumnLabels, isMobile, enrichPanelCollapsed,
      setEnrichPanelCollapsed, companies: newResults.companies, showResultsEmptyState, enrichmentGridRef,
      newCompanyIds: newResults.newCompanyIds, flashNewResultRows: newResults.flashNewResultRows,
      handleSpreadsheetSelectionChange, workspaceEnrichmentStatusMap, workspaceEnrichmentSummaryStatusMap, focusGeneratedColumnKey,
      snapshotPagination, historicalRows: historical.rows, historicalColumns: historical.columns,
      historicalLoading: historical.loading, historicalError: historical.error,
      loadMoreHistorical: historical.loadMore,
      handleFocusGeneratedColumnConsumed, deleteDialogOpen, handleDelete, openSaveSearchDialog,
      activeQuery, handleSavedSearchCreated, saveSearchSnackbar, setSaveSearchSnackbar,
      livePager: { page: livePaging.page, rowsPerPage: livePaging.rowsPerPage, totalRows, lastReachablePage: livePaging.lastReachablePage, disabled: searchLoading, onPageChange: handleLivePageChange, onRowsPerPageChange: livePaging.setRowsPerPage },
      onboardingBanner: <OnboardingActionsBanner {...onboardingActions} />
    }} />;
}
